import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { isAccountDisabledError } from '../core/account-status';
import { ApisService } from './apis.service';
import { ShiftService } from './shift.service';
import { UtilService } from './util.service';

/**
 * El cierre de sesión de una cuenta suspendida (NV-31), en un solo sitio.
 *
 * Lo usan el login, el `AuthGuard`, el observador de sesión (`start()`) y los listeners en
 * vivo (`handleError()`): una comanda abierta todo el día en una tablet no navega, así que el
 * guard solo no alcanza.
 */
@Injectable({ providedIn: 'root' })
export class SessionGuardService {
  private started = false;
  private hadSession = false;
  private ending = false;

  constructor(
    private api: ApisService,
    private fireAuth: AngularFireAuth,
    private shift: ShiftService,
    private util: UtilService,
    private router: Router
  ) {}

  /**
   * Cierra la sesión, apaga el turno (alerta y Wake Lock), limpia `uid`/`help`, avisa
   * «Cuenta suspendida» y lleva al login. Varios listeners pueden fallar a la vez: corre una vez.
   */
  async endSuspendedSession(): Promise<void> {
    if (this.ending) {
      return;
    }
    this.ending = true;
    try {
      this.shift.close();
      await this.api.logout().catch(() => undefined);
      localStorage.removeItem('uid');
      localStorage.removeItem('help');
      void this.util.showToast(this.util.translate('Account suspended'), 'danger', 'bottom');
      void this.router.navigate(['login']);
    } finally {
      this.ending = false;
    }
  }

  /** Para el `error` de un listener: si es de cuenta deshabilitada o sin refresh, cierra. */
  handleError(error: unknown): boolean {
    if (!isAccountDisabledError(error)) {
      return false;
    }
    void this.endSuspendedSession();
    return true;
  }

  /**
   * Observador único de la sesión de Auth. Un usuario que pasa a `null` sin que la app haya
   * cerrado la sesión (`api.signingOut`) es el SDK desloguéandolo porque la cuenta se
   * deshabilitó o el refresh token se revocó: mismo cierre.
   */
  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.fireAuth.authState.subscribe((user) => {
      if (user) {
        //  Toda sesion nueva (login, registro, restaurada) vuelve a quedar vigilada: la marca
        //  de un logout anterior no debe apagar el aviso de una expulsion posterior.
        this.api.signingOut = false;
        this.hadSession = true;
        return;
      }
      if (this.hadSession && !this.api.signingOut) {
        this.hadSession = false;
        void this.endSuspendedSession();
      }
    });
  }
}
